<template>
  <div class="known-business-entity-model-edit-form">
    <div
      class="w-full space-y-2 rounded-md"
      :class="isCreating || !useKVKSearch ? 'primary-lighten-1 p-2' : ''"
    >
      <!-- Switch between KVK search and manual entry -->
      <div v-if="isCreating" class="flex items-center justify-between">
        <span class="text-sm font-medium">Zoek in handelsregister</span>
        <InputSwitch
          v-model="useKVKSearch"
          @change="onUseKVKSearchChange"
          title="Wissel tussen KVK zoeken en handmatig invoeren"
        >
          <span class="sr-only">Wissel tussen KVK zoeken en handmatig invoeren</span>
        </InputSwitch>
      </div>

      <!-- KVK Search Mode -->
      <div v-show="useKVKSearch" class="space-y-2">
        <label v-if="isCreating" class="block">
          <span class="sr-only block text-sm">KVK-nummer</span>
          <InputCombobox
            v-model="selectedKVKItem"
            :items="KVKSearchResults"
            items-key-name="vestigingsnummer"
            :items-value-function="(item: any) => item.naam"
            :text-on-mounted="kvkTextOnMounted"
            input-placeholder="Bedrijfsnaam in het handelsregister"
            @query-changed="onKVKQueryChanged"
            @item-selected="onKVKItemSelected"
            :items-filter-fn="(item) => item.adres"
            :max-item-count="100"
            :sort-alphabetically="false"
            :editable="false"
            ref="comboboxKVK"
            grow
          >
            <template #top-of-options>
              <div class="gray-lighten-2 rounded-md p-2">Resultaten uit het handelsregister</div>
            </template>
            <template #custom-list-item="{ item, active }">
              <div
                class="py-2 pl-2"
                :class="{
                  'primary cursor-pointer': active
                }"
              >
                <div>{{ item.naam }}</div>
                <div class="text-sm" :class="active ? 'text-gray-lighten-2/50' : ''">
                  {{ item.kvkNummer }} - {{ item.adres?.binnenlandsAdres?.straatnaam }},
                  {{ item.adres?.binnenlandsAdres?.plaats }}
                </div>
              </div>
            </template>
          </InputCombobox>
        </label>

        <PAlert
          v-if="inputHasBeenGiven && KVKSearchResults.length === 0 && !isSearchingKVK"
          :type="AlertTypes.WARNING"
        >
          Geen resultaten gevonden in het handelsregister.
          <template #actions="{ buttonColor }">
            <GeneralButton :color="buttonColor" @click="switchToManualEntry">
              Handmatig toevoegen
            </GeneralButton>
          </template>
        </PAlert>

        <!-- Selected KVK result -->
        <div
          v-else-if="selectedKVKItem && selectedKVKMainAddress"
          class="gray-lighten-2 business-details rounded-md p-2 text-sm select-none"
        >
          <div class="flex flex-col">
            <span class="font-semibold">{{ selectedKVKItem.naam }}</span>
            <span>
              {{ selectedKVKMainAddress.volledigAdres }} <br />
              {{ selectedKVKMainAddress.land }}
            </span>
          </div>
          <div class="business-registration">
            <span class="text-xs font-semibold">KVK-nummer</span>
            <span>
              {{ selectedKVKItem.kvkNummer }}
            </span>
          </div>
        </div>

        <!-- Existing registered business entity -->
        <div
          v-if="
            knownBusinessEntity.registeredBusinessEntity &&
            !(selectedKVKItem && selectedKVKMainAddress)
          "
          class="gray-lighten-2 business-details rounded-md p-2 text-sm select-none"
        >
          <div class="flex flex-col">
            <span class="font-semibold">{{ knownBusinessEntity.name }}</span>
            <span>
              {{
                knownBusinessEntity.registeredBusinessEntity.getMainBusinessLocation().location
                  .address
              }}
              <br />
              {{
                countries.getName(
                  knownBusinessEntity.registeredBusinessEntity.countryOfRegistration,
                  "nl",
                  { select: "alias" }
                ) ?? knownBusinessEntity.registeredBusinessEntity.countryOfRegistration
              }}
            </span>
          </div>
          <div class="business-registration">
            <span class="text-xs font-semibold">KVK-nummer</span>
            <span>
              {{ knownBusinessEntity.registeredBusinessEntity.registrationNumber }}
            </span>
          </div>
        </div>
      </div>

      <!-- Manual Entry Mode -->
      <div v-show="!useKVKSearch">
        <div class="space-y-2">
          <label class="block">
            <span
              class="after:text-secondary sr-only mb-2 block text-xs font-semibold select-none after:content-['*']"
            >
              Land
            </span>
            <InputCombobox
              v-model="selectedCountryOption"
              :items="countryOptions"
              items-key-name="value"
              items-value-name="label"
              input-placeholder="Selecteer land"
              @item-selected="onCountrySelected"
              :items-filter-fn="filterCountry"
              :max-item-count="50"
              :sort-alphabetically="true"
              grow
              :errors="v$.countryOfRegistration.$errors"
              @focusout="v$.countryOfRegistration.$touch()"
            ></InputCombobox>
          </label>

          <label class="block">
            <span
              class="after:text-secondary sr-only mb-2 block text-xs font-semibold select-none after:content-['*']"
            >
              Bedrijfsnaam
            </span>
            <InputText
              v-model="knownBusinessEntity.customName"
              placeholder="Bedrijfsnaam"
              :errors="v$.customName.$errors"
              @focusout="v$.customName.$touch()"
            />
          </label>

          <label class="block">
            <span
              class="after:text-secondary sr-only mb-2 block text-xs font-semibold select-none after:content-['*']"
            >
              Registratienummer
            </span>
            <InputText
              v-model="knownBusinessEntity.registrationNumber"
              placeholder="Bedrijfsregistratienummer"
              :errors="v$.registrationNumber.$errors"
              @focusout="v$.registrationNumber.$touch()"
            />
          </label>
        </div>
      </div>
    </div>

    <div class="primary-lighten-1 mt-2 w-full space-y-2 rounded-md p-2">
      <label class="block">
        <span class="mb-2 block text-xs font-semibold select-none">Mandagenregister</span>
        <InputSelect
          v-model="selectedManDaysRegisterMode"
          :items="manDaysRegisterModeOptions"
          items-key-name="key"
          items-value-name="label"
          placeholder="Selecteer registratiewijze"
        />
      </label>
    </div>

    <section
      class="primary-lighten-1 mt-2 w-full rounded-md p-2"
      aria-labelledby="customer-approval-title"
      ref="approvalSettings"
    >
      <h3 id="customer-approval-title" class="mb-2 text-xs font-semibold select-none">
        Accordering
      </h3>
      <div class="gray-lighten-2 space-y-3 rounded-md p-2">
        <label class="block">
          <span class="mb-1.5 block text-xs font-semibold select-none">
            Vooraf akkoord op factuur
            <span v-if="creationParameters.requireApprovalMode" class="text-secondary">*</span>
          </span>
          <InputSelect
            v-model="selectedApprovalMode"
            :items="approvalModeOptions"
            items-key-name="key"
            items-value-name="label"
            placeholder="Maak een keuze"
            class="text-left"
          />
        </label>

        <template v-if="knownBusinessEntity.approvalMode === 'required'">
          <div class="approval-requirements text-sm">
            <InputSwitch v-model="knownBusinessEntity.approvalAmountRequired">
              Bedrag verplicht
            </InputSwitch>
            <InputSwitch v-model="knownBusinessEntity.approvalFileRequired">
              Bijlage verplicht
            </InputSwitch>
          </div>

          <div class="space-y-3">
            <hr class="border-primary-lighten-1/25" />
            <div class="approval-reference-row">
              <label class="block">
                <span class="mb-1.5 block text-xs font-semibold select-none"
                  >Label referentie 1
                  <span class="font-normal">(optioneel)</span>
                </span>
                <InputText
                  v-model="knownBusinessEntity.approvalReferenceLabel"
                  maxlength="100"
                  :errors="v$.approvalReferenceLabel.$errors"
                  @focusout="v$.approvalReferenceLabel.$touch()"
                />
              </label>
              <InputSwitch
                v-model="knownBusinessEntity.approvalReferenceRequired"
                :inert="!knownBusinessEntity.approvalReferenceLabel.trim() || undefined"
                :class="{ 'opacity-45': !knownBusinessEntity.approvalReferenceLabel.trim() }"
                class="text-sm"
              >
                <span aria-hidden="true">Verplicht bij akkoord</span>
                <span class="sr-only">Referentie 1 verplicht bij akkoord</span>
              </InputSwitch>
            </div>

            <ReferenceFormatInput
              v-model:format="knownBusinessEntity.approvalReferenceFormat"
              configurable
              :disabled="!knownBusinessEntity.approvalReferenceLabel.trim()"
              placeholder="Geen format"
              aria-label="Format referentie 1"
            />

            <div class="approval-reference-row">
              <label class="block">
                <span class="mb-1.5 block text-xs font-semibold select-none"
                  >Label referentie 2 <span class="font-normal">(optioneel)</span></span
                >
                <InputText v-model="knownBusinessEntity.approvalReference2Label" maxlength="100" />
              </label>
              <InputSwitch
                v-model="knownBusinessEntity.approvalReference2Required"
                :inert="!knownBusinessEntity.approvalReference2Label.trim() || undefined"
                :class="{ 'opacity-45': !knownBusinessEntity.approvalReference2Label.trim() }"
                class="text-sm"
              >
                <span aria-hidden="true">Verplicht bij akkoord</span>
                <span class="sr-only">Referentie 2 verplicht bij akkoord</span>
              </InputSwitch>
            </div>
            <ReferenceFormatInput
              v-model:format="knownBusinessEntity.approvalReference2Format"
              configurable
              :disabled="!knownBusinessEntity.approvalReference2Label.trim()"
              placeholder="Geen format"
              aria-label="Format referentie 2"
            />
          </div>
        </template>
      </div>
    </section>

    <div ref="invoicingSettingsSection">
      <CustomerInvoicingSettings
        :key="knownBusinessEntity.id"
        v-model="knownBusinessEntity.invoicingSettings"
      />
    </div>

    <FormValidationAlert v-model="v$" />
  </div>
</template>
<script setup lang="ts">
import { computed, inject, nextTick, onMounted, ref, type Ref, useTemplateRef, watch } from "vue"
import CustomerInvoicingSettings from "./CustomerInvoicingSettings.vue"
import { createCustomerInvoicingSettings } from "@/interfaces/api/customer-invoicing-settings.interface"
import ReferenceFormatInput from "@/components/inputs/ReferenceFormatInput.vue"
import InputCombobox from "@/components/inputs/input_combobox/InputCombobox.vue"
import InputText from "@/components/inputs/InputText.vue"
import InputSelect from "@/components/inputs/InputSelect.vue"
import GeneralButton from "@/components/inputs/GeneralButton.vue"
import InputSwitch from "@/components/inputs/InputSwitch.vue"
import PAlert from "@/components/elements/PAlert.vue"
import { AlertTypes } from "@/interfaces/component/alert.interface"
import { KnownBusinessEntity } from "@/models/known-business-entity.model.ts"
import { ManDaysRegisterMode } from "@/enums/man-days-register-mode.enum.ts"
import { KVKRepository } from "@/repositories/external/kvk-repository"
import { countryAndRegistrationNumberIsUniqueValidator } from "@/helper/validation/known-business-entity-validation.ts"
import type { KnownBusinessEntityController } from "@/controllers/model-controllers/known-business-entity-controller.ts"
import { useFormConfigurator } from "@/composables/form/form-configurator"
import countries from "i18n-iso-countries"
import nl from "i18n-iso-countries/langs/nl.json"
import FormValidationAlert from "@/components/validation/FormValidationAlert.vue"
import { requiredValidator } from "@/helper/validation/validators.ts"
import type { ApprovalMode } from "@/interfaces/api/known-business-entity.interface.ts"

const useKVKSearch = ref(true)

// The agreement-card customer picker requires an explicit policy before selection.
const creationParameters =
  inject<{
    KVKResult?: any
    requireApprovalMode?: boolean
    focusApprovalSettings?: boolean
    focusInvoicingSettings?: boolean
  }>("creationParameters") ?? {}
const invoicingSettingsSection = useTemplateRef<HTMLDivElement>("invoicingSettingsSection")

const rules = computed(() => {
  const _rules = {
    customName: {},
    countryOfRegistration: {},
    registrationNumber: {},
    approvalMode: creationParameters.requireApprovalMode ? { requiredValidator } : {},
    approvalReferenceLabel: {},
    $validator: countryAndRegistrationNumberIsUniqueValidator
  }

  if (!useKVKSearch.value) {
    _rules.customName = { requiredValidator }
    _rules.countryOfRegistration = { requiredValidator }
    _rules.registrationNumber = { requiredValidator }
  }

  return _rules
})

const controller: KnownBusinessEntityController = inject("knownBusinessEntityController")!

const {
  model: knownBusinessEntity,
  isCreating,
  initialText,
  v$
} = useFormConfigurator<KnownBusinessEntity>({
  controller,
  rules,
  customSaveHandler: onSave
})
// The form wrapper makes a shallow model copy. Detach these nested settings so
// closing without saving cannot mutate the customer in the shared model store.
knownBusinessEntity.value.invoicingSettings = createCustomerInvoicingSettings(
  knownBusinessEntity.value.invoicingSettings
)
const isFormInvalid: Ref<boolean> = inject("isFormInvalid")!

const KVKSearchResults = ref([])
const isSearchingKVK = ref(false)
const lastSearch = ref("")
const inputHasBeenGiven = ref(false)

countries.registerLocale(nl)

const selectedKVKItem = ref<any | null>(null)

const selectedKVKMainAddress = ref<any | null>(null)

// Check whether a company has been selected
const isCompanySelected = computed(() => {
  if (useKVKSearch.value) {
    // KVK mode: just check if we have a selected result
    return !!selectedKVKItem.value
  } else {
    // Manual mode: make sure all required fields are filled
    return !!(
      knownBusinessEntity.value.countryOfRegistration &&
      knownBusinessEntity.value.registrationNumber &&
      knownBusinessEntity.value.customName
    )
  }
})

// Update form invalid state based on company selection and validation
watch(
  [isCompanySelected, () => v$.value.$invalid],
  ([hasCompany, isInvalid]) => {
    if (isCreating.value) {
      // When creating, form is invalid if no company is selected or validation fails
      isFormInvalid.value = !hasCompany || isInvalid
    } else {
      // When editing, only check validation
      isFormInvalid.value = isInvalid
    }
  },
  { immediate: true }
)

const kvkTextOnMounted = computed(() => {
  if (isCreating.value) {
    return initialText ?? ""
  }
  return knownBusinessEntity.value.registeredBusinessEntity?.name
    ? knownBusinessEntity.value.name
    : ""
})

const countryOptions = computed(() => {
  return Object.entries(countries.getNames("nl", { select: "alias" })).map(([code, name]) => ({
    value: code,
    label: name
  }))
})
const selectedCountryOption = ref()
function onCountrySelected(option: any) {
  knownBusinessEntity.value.countryOfRegistration = option?.value
}

// Mandagenregister mode selection
const manDaysRegisterModeOptions = [
  { key: ManDaysRegisterMode.NEVER, label: "Nooit" },
  { key: ManDaysRegisterMode.SEPARATE_ATTACHMENT, label: "Als aparte bijlage" },
  { key: ManDaysRegisterMode.IN_STATEMENT_PDF, label: "In de verzamelstaat (PDF)" }
]

const selectedManDaysRegisterMode = computed({
  get: () =>
    manDaysRegisterModeOptions.find(
      (opt) => opt.key === knownBusinessEntity.value.manDaysRegisterMode
    ) ?? manDaysRegisterModeOptions[0],
  set: (option) => {
    knownBusinessEntity.value.manDaysRegisterMode = option.key
  }
})

const approvalModeOptions: { key: ApprovalMode; label: string }[] = [
  { key: "", label: "Maak een keuze" },
  { key: "none", label: "Nee" },
  { key: "required", label: "Ja" }
]

const selectedApprovalMode = computed({
  get: () =>
    approvalModeOptions.find((option) => option.key === knownBusinessEntity.value.approvalMode) ??
    null,
  set: (option) => {
    if (!option) return
    const customer = knownBusinessEntity.value
    customer.approvalMode = option.key
    customer.approvalDefaultMethod = option.key ? "other" : ""
    if (option.key !== "required") {
      customer.approvalAmountRequired = false
      customer.approvalFileRequired = false
      customer.approvalReferenceRequired = false
      customer.approvalReference2Required = false
      customer.approvalReferenceLabel = ""
      customer.approvalReference2Label = ""
      customer.approvalReferenceFormat = null
      customer.approvalReference2Format = null
    }
  }
})

watch(
  () => [
    knownBusinessEntity.value.approvalReferenceLabel,
    knownBusinessEntity.value.approvalReference2Label
  ],
  ([firstLabel, secondLabel]) => {
    if (!firstLabel.trim()) {
      knownBusinessEntity.value.approvalReferenceRequired = false
      knownBusinessEntity.value.approvalReferenceFormat = null
    }
    if (!secondLabel.trim()) {
      knownBusinessEntity.value.approvalReference2Required = false
      knownBusinessEntity.value.approvalReference2Format = null
    }
  },
  { immediate: true }
)

// Filter function for country combobox
function filterCountry(item: any, query: string): boolean {
  const queryLower = query.toLowerCase()
  return (
    item.value.toLowerCase().includes(queryLower) || item.label.toLowerCase().includes(queryLower)
  )
}

async function searchKVK(searchTerm: string) {
  if (!searchTerm || searchTerm.trim().length === 0) {
    KVKSearchResults.value = []
    return
  }

  isSearchingKVK.value = true
  const result = await new KVKRepository().searchByName(searchTerm)
  isSearchingKVK.value = false
  if (result.resultaten) {
    KVKSearchResults.value = result.resultaten
    // Ensure combobox opens to show results after search completes
    if (comboboxKVK.value && KVKSearchResults.value.length > 0) {
      comboboxKVK.value.setForceOpenState(true)
    }
  } else {
    KVKSearchResults.value = []
  }
  lastSearch.value = searchTerm
}

let searchTimeout: ReturnType<typeof setTimeout> | null = null
function onKVKQueryChanged(query: string) {
  if (selectedKVKItem.value && query !== selectedKVKItem.value?.naam) {
    // If the user edits the query after selecting an item, treat it as "no selection" again.
    selectedKVKItem.value = null
    selectedKVKMainAddress.value = null
  }
  if (searchTimeout) {
    clearTimeout(searchTimeout)
    searchTimeout = null
  }
  searchTimeout = setTimeout(() => {
    inputHasBeenGiven.value = query.length > 0
    searchKVK(query)
  }, 250)
}

const comboboxKVK = useTemplateRef<typeof InputCombobox>("comboboxKVK")
const approvalSettings = useTemplateRef<HTMLElement>("approvalSettings")

function focusCombobox() {
  comboboxKVK.value?.focus()
}

async function focusApprovalSettings() {
  await nextTick()
  comboboxKVK.value?.setForceOpenState(false)
  approvalSettings.value?.scrollIntoView({ block: "nearest" })
}

async function onKVKResultSelected(KVKResult: any) {
  const result = await new KVKRepository().getEstablishmentDetails(KVKResult.vestigingsnummer)
  if (result.adressen) {
    selectedKVKMainAddress.value = result.adressen.find(
      (address: any) => address.type === "bezoekadres"
    )
  }
}

function onKVKItemSelected(item: any) {
  selectedKVKItem.value = item
  onKVKResultSelected(item)
  // Close the dropdown when an item is selected
  if (comboboxKVK.value) {
    comboboxKVK.value.setForceOpenState(false)
    comboboxKVK.value.blur()
  }
}

function onUseKVKSearchChange(value: boolean) {
  if (!value) {
    knownBusinessEntity.value.customName = undefined
  } else {
    // Use the initial text as a starting point for the custom name
    knownBusinessEntity.value.customName = lastSearch.value
  }
}

function switchToManualEntry() {
  useKVKSearch.value = false
  onUseKVKSearchChange(false)
}

async function onSave(save: () => Promise<void>) {
  if (!isCreating.value) {
    // When editing an existing entity we only persist the editable fields
    // (e.g. the man-days register mode) via the standard update flow.
    // Re-creating the entity from its registration number makes no sense here.
    await save()
    return
  }

  const settings = {
    invoicingSettings: knownBusinessEntity.value.invoicingSettings,
    manDaysRegisterMode: knownBusinessEntity.value.manDaysRegisterMode,
    approvalMode: knownBusinessEntity.value.approvalMode,
    approvalDefaultMethod: knownBusinessEntity.value.approvalDefaultMethod,
    approvalAmountRequired: knownBusinessEntity.value.approvalAmountRequired,
    approvalFileRequired: knownBusinessEntity.value.approvalFileRequired,
    approvalReferenceRequired: knownBusinessEntity.value.approvalReferenceRequired,
    approvalReferenceLabel: knownBusinessEntity.value.approvalReferenceLabel,
    approvalReferenceFormat: knownBusinessEntity.value.approvalReferenceFormat,
    approvalReference2Required: knownBusinessEntity.value.approvalReference2Required,
    approvalReference2Label: knownBusinessEntity.value.approvalReference2Label,
    approvalReference2Format: knownBusinessEntity.value.approvalReference2Format
  }
  let result: KnownBusinessEntity | undefined
  if (useKVKSearch.value) {
    // KVK search mode
    result = await controller.createFromRegistrationNumber("NL", selectedKVKItem.value.kvkNummer)
  } else {
    if (
      !knownBusinessEntity.value.countryOfRegistration ||
      !knownBusinessEntity.value.registrationNumber ||
      !knownBusinessEntity.value.customName
    ) {
      throw new Error("All fields are required")
    }
    // Manual input mode
    result = await controller.createFromRegistrationNumber(
      knownBusinessEntity.value.countryOfRegistration,
      knownBusinessEntity.value.registrationNumber,
      knownBusinessEntity.value.customName
    )
  }

  if (!result) {
    throw new Error("Het bedrijf kon niet worden opgeslagen.")
  }
  // The registration endpoint creates the company; its settings are persisted by the normal update.
  Object.assign(knownBusinessEntity.value, result, settings)
  await save()
}

onMounted(() => {
  if (!isCreating.value && creationParameters.focusInvoicingSettings) {
    selectedKVKMainAddress.value = knownBusinessEntity.value.registeredBusinessEntity
    if (!knownBusinessEntity.value.registeredBusinessEntity?.name) useKVKSearch.value = false
    nextTick(() => invoicingSettingsSection.value?.scrollIntoView({ block: "start" }))
    return
  }
  if (isCreating.value) {
    setTimeout(async () => {
      if (initialText && initialText.length > 0) {
        if (creationParameters?.KVKResult) {
          // In case the KVK result was preselected, use it directly, no need to search
          selectedKVKItem.value = creationParameters.KVKResult
          await onKVKResultSelected(creationParameters.KVKResult)
        } else {
          // Cancel any pending search to avoid duplicates
          if (searchTimeout) {
            clearTimeout(searchTimeout)
            searchTimeout = null
          }
          inputHasBeenGiven.value = true
          // Search immediately when form opens with text
          await searchKVK(initialText)
        }

        if (creationParameters.focusApprovalSettings) {
          await focusApprovalSettings()
          return
        }

        // FOCUS HACK: yes, twice
        requestAnimationFrame(() => {
          requestAnimationFrame(async () => {
            focusCombobox()
            // again, twice
            await nextTick()
            await nextTick()

            // Move cursor to the end of the input
            const comboboxElement = comboboxKVK.value?.$el
            const inputElement = comboboxElement?.querySelector("input") as HTMLInputElement
            inputElement?.setSelectionRange(inputElement.value.length, inputElement.value.length)

            // Highlight first option by simulating arrow down key
            inputElement?.dispatchEvent(
              new KeyboardEvent("keydown", { key: "ArrowDown", bubbles: true })
            )
            inputElement?.dispatchEvent(
              new KeyboardEvent("keyup", { key: "ArrowDown", bubbles: true })
            )
          })
        })
      } else if (creationParameters.focusApprovalSettings) {
        await focusApprovalSettings()
      }
    }, 0)
  } else {
    if (knownBusinessEntity.value.registeredBusinessEntity?.name) {
      setTimeout(async () => {
        selectedKVKMainAddress.value = knownBusinessEntity.value.registeredBusinessEntity
        if (creationParameters.focusApprovalSettings) {
          await focusApprovalSettings()
        } else {
          focusCombobox()
        }
      }, 0)
    } else {
      useKVKSearch.value = false
      // Set the country dropdown to the existing value if we have one
      if (knownBusinessEntity.value.countryOfRegistration) {
        selectedCountryOption.value = countryOptions.value.find(
          (opt) => opt.value === knownBusinessEntity.value.countryOfRegistration
        )
      }
      if (creationParameters.focusApprovalSettings) {
        nextTick(focusApprovalSettings)
      }
    }
  }
})
</script>

<style scoped>
.business-details {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 108px;
  gap: 16px;
  line-height: 20px;
}

.business-registration {
  display: flex;
  flex-direction: column;
}

.approval-requirements {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 24px;
}

.approval-reference-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 196px;
  align-items: end;
  gap: 6px 16px;
}

.approval-reference-row > .input-switch {
  align-self: start;
  padding-top: 24px;
}

@media (max-width: 480px) {
  .business-details,
  .approval-reference-row {
    grid-template-columns: minmax(0, 1fr);
  }

  .business-details {
    gap: 8px;
  }

  .business-registration {
    flex-direction: row;
    align-items: baseline;
    gap: 8px;
    font-size: 12px;
  }

  .approval-requirements {
    flex-direction: column;
  }

  .approval-reference-row > .input-switch {
    padding-top: 0;
  }
}
</style>
